import { ActivitySummaryMetric, GetVolunteerActivitySummaryResponse } from '@amzn/red-velvet-api';
import { getStageByOrigin } from '../../../utils/requestConfig';

// Fixtures for reviewing the summary's edge and failure states on non-prod stages, through
// ?activitySummaryMock=<fixture>.

/** URL parameter that picks a fixture while reviewing the design, e.g. ?activitySummaryMock=partial */
export const ACTIVITY_SUMMARY_MOCK_PARAM = 'activitySummaryMock';

// Matches the LLD example response, which is also the Figma frame (6 / 19 shifts, 12 hours).
const DEFAULT_FIXTURE: GetVolunteerActivitySummaryResponse = {
  summary: {
    year: 2026,
    volunteeringSince: new Date('2023-04-15T16:00:00Z'),
    shiftsCompleted: { thisYear: 6, allTime: 19 },
    volunteerMinutes: { thisYear: 720, allTime: 2280 },
    attendanceTracking: { tracked: 14, total: 18 },
    unavailableMetrics: [],
  },
};

/**
 * One fixture per display rule and failure state in the LLD, so each can be reviewed on alpha.
 * Every fixture has the exact shape of GetVolunteerActivitySummaryResponse.
 */
export const VOLUNTEER_ACTIVITY_SUMMARY_FIXTURES: Record<
  string,
  GetVolunteerActivitySummaryResponse
> = {
  default: DEFAULT_FIXTURE,
  // The signups read failed, so only the rate is left out.
  partial: {
    summary: {
      year: 2026,
      volunteeringSince: new Date('2023-04-15T16:00:00Z'),
      shiftsCompleted: { thisYear: 6, allTime: 19 },
      volunteerMinutes: { thisYear: 720, allTime: 2280 },
      unavailableMetrics: [ActivitySummaryMetric.ATTENDANCE_TRACKING],
    },
  },
  fullyTracked: {
    summary: {
      year: 2026,
      volunteeringSince: new Date('2024-02-03T17:00:00Z'),
      shiftsCompleted: { thisYear: 4, allTime: 9 },
      volunteerMinutes: { thisYear: 330, allTime: 810 },
      attendanceTracking: { tracked: 9, total: 9 },
      unavailableMetrics: [],
    },
  },
  firstYear: {
    summary: {
      year: 2026,
      volunteeringSince: new Date('2026-03-10T16:00:00Z'),
      shiftsCompleted: { thisYear: 1, allTime: 1 },
      volunteerMinutes: { thisYear: 90, allTime: 90 },
      attendanceTracking: { tracked: 1, total: 2 },
      unavailableMetrics: [],
    },
  },
  priorYearsOnly: {
    summary: {
      year: 2026,
      volunteeringSince: new Date('2022-09-01T16:00:00Z'),
      shiftsCompleted: { thisYear: 0, allTime: 7 },
      volunteerMinutes: { thisYear: 0, allTime: 1260 },
      attendanceTracking: { tracked: 7, total: 8 },
      unavailableMetrics: [],
    },
  },
  signedUpNothingTracked: {
    summary: {
      year: 2026,
      shiftsCompleted: { thisYear: 0, allTime: 0 },
      volunteerMinutes: { thisYear: 0, allTime: 0 },
      attendanceTracking: { tracked: 0, total: 1 },
      unavailableMetrics: [],
    },
  },
  nothingCompleted: {
    summary: {
      year: 2026,
      shiftsCompleted: { thisYear: 0, allTime: 0 },
      volunteerMinutes: { thisYear: 0, allTime: 0 },
      attendanceTracking: { tracked: 0, total: 0 },
      unavailableMetrics: [],
    },
  },
  allUnavailable: {
    summary: {
      year: 2026,
      unavailableMetrics: [
        ActivitySummaryMetric.VOLUNTEERING_SINCE,
        ActivitySummaryMetric.SHIFTS_COMPLETED,
        ActivitySummaryMetric.VOLUNTEER_MINUTES,
        ActivitySummaryMetric.ATTENDANCE_TRACKING,
      ],
    },
  },
  // No signups and no attendance: the API leaves out `summary`.
  neverParticipated: {},
};

/** Fixture name that makes the mock request fail, to review the whole-container failure state. */
export const ACTIVITY_SUMMARY_MOCK_ERROR = 'error';

// Personal dev stacks have no entry in config.tsx (the dev entry keeps a {user} placeholder), so
// they resolve to null. Beta and prod always resolve to their own stage and never get mocks.
const MOCK_STAGES: (string | null)[] = ['local', 'dev', 'alpha', null];

/** ITL targets (alpha-itl, alpha-2-itl, ...) share alpha's flag config, so they get mocks too. */
const isItlTarget = (stage: string | null): boolean => stage?.endsWith('-itl') ?? false;

/** Mock data must never reach beta or prod, even if the flag is turned on there by mistake. */
export function areActivitySummaryMocksAllowed(): boolean {
  const stage = getStageByOrigin();
  return MOCK_STAGES.includes(stage) || isItlTarget(stage);
}

/** Returns the named fixture (default when unknown), or throws for the error scenario. */
export function getVolunteerActivitySummaryMock(
  fixtureName: string | null
): GetVolunteerActivitySummaryResponse {
  if (fixtureName === ACTIVITY_SUMMARY_MOCK_ERROR) {
    throw new Error('Mock GetVolunteerActivitySummary failure');
  }
  return VOLUNTEER_ACTIVITY_SUMMARY_FIXTURES[fixtureName ?? ''] ?? DEFAULT_FIXTURE;
}
